import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Building2 } from "lucide-react";
import SEO from "@/components/SEO";

/**
 * MarketingPage: shared layout for About / About-for-Business style pages.
 *
 * Props:
 *  - testId, seo: { title, description, canonical }
 *  - hero: { kicker, title, sub }
 *  - sections: [{ h, body: [paragraph, ...] }]
 *  - audience: "renter" | "business": picks the hero icon + CTA bar.
 */
export default function MarketingPage({
  testId, seo, hero, sections, audience = "renter",
}) {
  const Icon = audience === "business" ? Building2 : ShieldCheck;
  const ctas = audience === "business"
    ? [
        { to: "/register?as=business", label: "Start with my next booking",
          variant: "primary", testId: "marketing-cta-trial" },
        { to: "/businesses/intake",     label: "See how ConfirmPH works",
          variant: "secondary", testId: "marketing-cta-how" },
        { to: "/businesses/pricing",    label: "See plans",
          variant: "ghost", testId: "marketing-cta-pricing" },
      ]
    : [
        { to: "/find-booking",          label: "Find my booking",
          variant: "primary", testId: "marketing-cta-find" },
        { to: "/request-confirmation",  label: "Request confirmation",
          variant: "secondary", testId: "marketing-cta-request" },
        { to: "/rentals",               label: "Discover rentals",
          variant: "ghost", testId: "marketing-cta-discover" },
      ];

  return (
    <div data-testid={testId} className="bg-[#FAFAFA] text-slate-900">
      {seo && <SEO {...seo} />}

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1B4332] via-[#1B4332] to-[#0d2c20] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 text-xs font-medium uppercase tracking-[0.2em]">
            <Icon className="w-3.5 h-3.5" /> {hero.kicker}
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            {hero.title}
          </h1>
          {hero.sub && (
            <p className="mt-5 text-emerald-50/90 text-base sm:text-lg max-w-3xl leading-relaxed">
              {hero.sub}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {ctas.map((c) => (
              <Link key={c.to} to={c.to} data-testid={c.testId}
                     className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                       c.variant === "primary"
                         ? "bg-emerald-400 text-emerald-950 hover:bg-emerald-300"
                         : c.variant === "secondary"
                         ? "border border-emerald-300/60 bg-transparent text-white hover:bg-emerald-400/10"
                         : "text-emerald-100 hover:text-white underline-offset-2 hover:underline"
                     }`}>
                {c.label}{c.variant !== "ghost" && <ArrowRight className="w-3.5 h-3.5" />}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-12">
        {sections.map((s, i) => (
          <article key={i} data-testid={`marketing-section-${i}`}>
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900">
              {s.h}
            </h2>
            <SectionBody paragraphs={s.body} />
          </article>
        ))}
      </section>

    </div>
  );
}


// ---------------------------------------------------------------------------
// SectionBody: turns a flat list of strings (as imported from the docx) into
// polished markup. Three normalisations:
//
//   1. Insert a missing space after `.` when followed by a capital letter:
//      "Facebook.A staff…" → "Facebook. A staff…"
//      "booking.The owner…" → "booking. The owner…"
//      (skips well-known acronym joins like "Mr.X": none in our corpus.)
//
//   2. Detect an embedded informal sub-heading at the start of a paragraph
//      and promote it to an <h3>. The heading list is closed and explicit
//      (KNOWN_HEADINGS): we never split on a generic capitalised phrase
//      because that would butcher real sentences mid-thought.
//
//   3. Group consecutive paragraphs that end with `;` (or that look like
//      bullet entries) into a single <ul>. This matches how the brand-
//      supplied .docx authors lists.
// ---------------------------------------------------------------------------

const KNOWN_HEADINGS = [
  "What ConfirmPH is",
  "Why ConfirmPH exists",
  "ConfirmPH does not replace them",
  "ConfirmPH is not the rental company",
  "ConfirmPH helps bring the important details into one place",
  "How ConfirmPH helps renters",
  "How ConfirmPH helps businesses",
  "Why renters use ConfirmPH",
  "Why customers like businesses that use ConfirmPH",
  "What customers should know",
  "What businesses should know",
  "Trust and safety",
];

function _normalize(text) {
  if (!text) return text;
  // Add a space between `.` and the next capital letter when the period is
  // attached to a real word (≥ 2 letters before the period) and the next
  // character is a capital that starts either a new word ("A booking") or
  // a multi-letter word ("Another"). This catches:
  //   "Facebook.A staff…"     → "Facebook. A staff…"
  //   "Messenger.Another"     → "Messenger. Another"
  //   "phone.A booking"       → "phone. A booking"
  return text.replace(
    /([a-zA-Z]{2,})\.([A-Z])(?=[A-Za-z]|\s)/g,
    "$1. $2",
  );
}

/**
 * Detect "lead: itemA; itemB; itemC." patterns inside a single paragraph
 * and split them into { lead, items[] }. Returns null when the paragraph
 * isn't a list. Heuristic: at least 3 semicolons inside the text body.
 */
function _explodeInlineList(text) {
  const colonIdx = text.indexOf(":");
  if (colonIdx < 5 || colonIdx > text.length - 10) return null;
  const head = text.slice(0, colonIdx).trim();
  const tail = text.slice(colonIdx + 1).trim();
  // Count semicolons in the tail.
  const parts = tail.split(/\s*;\s*/);
  if (parts.length < 4) return null;          // need at least 3 semicolons (4 parts)
  // Strip a final period from the last item (some docx authors end the list
  // with a full stop instead of a semicolon).
  const items = parts
    .map((p) => p.replace(/[.;,]+$/, "").trim())
    .filter(Boolean);
  if (items.length < 3) return null;
  return { lead: head + ":", items };
}

function _splitEmbeddedHeading(text) {
  // If the paragraph starts with a known heading + " " + sentence body,
  // return [heading, rest]. Otherwise [null, text].
  for (const h of KNOWN_HEADINGS) {
    if (text.startsWith(h + " ")) {
      const rest = text.slice(h.length + 1).trim();
      // Only split if the rest looks like a real sentence (length > 12 and
      // starts with a capital letter, to avoid splitting "How ConfirmPH
      // helps" away from a single word that follows it).
      if (rest.length > 12 && /^[A-Z(]/.test(rest)) {
        return [h, rest];
      }
    }
  }
  return [null, text];
}

function _looksLikeBullet(text) {
  // Bullet-list items in our docx imports end with `;` (the author's style).
  // Treat any paragraph ending in `;` as a list item.  Also treat a
  // paragraph ending in `:` IFF the FOLLOWING paragraph starts a bullet
  // run, but that's handled at the group level.
  const t = (text || "").trim();
  if (!t) return false;
  if (t.endsWith(";")) return true;
  return false;
}

export function SectionBody({ paragraphs }) {
  // Expand each input paragraph into one or more (possibly heading) units.
  const units = [];
  for (const raw of paragraphs || []) {
    const cleaned = _normalize((raw || "").trim());
    if (!cleaned) continue;
    const [embedded, rest] = _splitEmbeddedHeading(cleaned);
    let textToEmit = cleaned;
    if (embedded) {
      units.push({ kind: "h3", text: embedded });
      textToEmit = rest;
      if (!textToEmit) continue;
    }
    // After heading-strip, look for an inline "lead: a; b; c." list.
    const exploded = _explodeInlineList(textToEmit);
    if (exploded) {
      units.push({ kind: "p", text: exploded.lead });
      for (const it of exploded.items) {
        units.push({ kind: "li", text: it });
      }
      continue;
    }
    if (_looksLikeBullet(textToEmit)) {
      // Strip the trailing semicolon: UI shows the bullet glyph.
      units.push({ kind: "li", text: textToEmit.replace(/;$/, "").trim() });
    } else {
      units.push({ kind: "p", text: textToEmit });
    }
  }

  // Walk units, grouping consecutive `li`s under a single <ul>.
  const out = [];
  let bucket = null;
  for (let i = 0; i < units.length; i += 1) {
    const u = units[i];
    if (u.kind === "li") {
      if (!bucket) {
        bucket = { kind: "ul", items: [] };
        out.push(bucket);
      }
      bucket.items.push(u.text);
    } else {
      bucket = null;
      out.push(u);
    }
  }

  return (
    <div className="mt-3 space-y-4 text-slate-700 leading-relaxed text-[15px]">
      {out.map((u, idx) => {
        if (u.kind === "h3") {
          return (
            <h3 key={idx}
                  className="mt-6 font-display text-base sm:text-lg font-semibold text-slate-900">
              {u.text}
            </h3>
          );
        }
        if (u.kind === "ul") {
          return (
            <ul key={idx}
                 className="list-disc pl-5 space-y-1.5 marker:text-emerald-600">
              {u.items.map((it, j) => (
                <li key={j}>{it}</li>
              ))}
            </ul>
          );
        }
        return <p key={idx}>{u.text}</p>;
      })}
    </div>
  );
}
